<template>
  <div v-if="visible" class="tw">
    <!-- Lock banner (VR-015 / ERR-015) -->
    <AlertBar
      v-if="isLocked"
      type="info"
      variant="tonal"
      class="mt-0 mx-6 mb-4"
      data-cy="fee-recipients-locked-banner"
    >
      <span class="font-weight-bold">Fee recipients are locked.</span>
      The settlement has been finalized. Restart the settlement flow to make changes.
    </AlertBar>

    <!-- Skeleton loader (initial load only). Once locked, also held open until the
         disbursements mount chain settles (initialLoadPending) — otherwise the table paints
         once with every row's Status cell in a transient loading spinner, then repaints a beat
         later with the real Submit/Undo controls, reading as the table "readjusting" itself.
         Painting once, fully-formed, costs a slightly longer initial wait but no visible jolt. -->
    <template v-if="initialLoadPending">
      <div data-testid="skeleton" class="py-5 px-6">
        <v-skeleton-loader type="heading" class="mb-4" elevation="0" />
        <v-skeleton-loader type="table-row-divider@3" class="w-full" elevation="0" />
      </div>
    </template>

    <div v-else class="py-5 px-6">
      <!-- Header row -->
      <div class="flex items-center justify-between mb-4">
        <div>
          <div class="text-base font-bold text-[#1a1a1a] mb-0.5">Fee Recipients</div>
          <div class="text-xs text-[#666]">
            Manage co-counsel and referring attorney fee splits for this matter. Recipients auto-load
            into Step 3 of the Settlement Wizard .
          </div>
        </div>
        <LlTooltip location="top" :disabled="!addDisabledReason">
          <template #default>{{ addDisabledReason }}</template>
          <template #activator>
            <LlSecondaryButton
              prepend-icon="mdi-plus"
              color="primary"
              size="small"
              :disabled="!!addDisabledReason"
              data-cy="add-fee-recipient"
              @click="editor.startAdd()"
            >Add Recipient</LlSecondaryButton>
          </template>
        </LlTooltip>
      </div>

      <!-- Recipients table — always rendered, even with zero recipients (see empty-state row below). -->
      <div class="overflow-x-auto">
        <table class="w-full border-collapse text-[12.5px]">
          <thead>
            <tr class="bg-[#e7f6ea] border-b-2 border-[#c6ebcf]">
              <th class="px-2.5 py-2 text-left font-semibold text-[#1b5e20] whitespace-nowrap min-w-[180px]">Recipient Name</th>
              <th class="px-2.5 py-2 text-left font-semibold text-[#1b5e20] whitespace-nowrap min-w-[180px]">Role</th>
              <th class="px-2.5 py-2 text-right font-semibold text-[#1b5e20] whitespace-nowrap w-[140px]">
                % of Total Fee
                <LlTooltip location="top">
                  <template #default>Attorney fees round down to the nearest cent; any leftover goes to your firm's share so the client is never overcharged.</template>
                  <template #activator><span class="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border-[1.3px] border-current text-[9px] not-italic font-bold ml-1 align-middle cursor-help" data-cy="fee-recipient-percentage-info">i</span></template>
                </LlTooltip>
              </th>
              <th class="px-2.5 py-2 text-right font-semibold text-[#1b5e20] whitespace-nowrap">
                Fee Amount
                <LlTooltip v-if="!isLocked" location="top">
                  <template #default>Calculated when the settlement is finalized based on the recovery amount.</template>
                  <template #activator><span class="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border-[1.3px] border-current text-[9px] not-italic font-bold ml-1 align-middle cursor-help">i</span></template>
                </LlTooltip>
              </th>
              <!-- Expenses carries a second, arrangement-specific note (BR-020): under Client Pays As
                   They Go the figure means something different, so that guidance replaces the generic
                   "calculated on finalization" note rather than sitting beside it as a second dot. -->
              <th class="px-2.5 py-2 text-right font-semibold text-[#1b5e20] whitespace-nowrap">
                Expenses
                <LlTooltip v-if="expensesTooltip" location="top">
                  <template #default>{{ expensesTooltip }}</template>
                  <template #activator><span class="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border-[1.3px] border-current text-[9px] not-italic font-bold ml-1 align-middle cursor-help" data-cy="fee-recipient-expenses-info">i</span></template>
                </LlTooltip>
              </th>
              <th class="px-2.5 py-2 text-right font-semibold text-[#1b5e20] whitespace-nowrap border-l border-[#c6ebcf]">
                Total
                <LlTooltip v-if="!isLocked" location="top">
                  <template #default>Calculated when the settlement is finalized based on the recovery amount.</template>
                  <template #activator><span class="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border-[1.3px] border-current text-[9px] not-italic font-bold ml-1 align-middle cursor-help">i</span></template>
                </LlTooltip>
              </th>
              <!-- Status is meaningful only once the settlement is locked — hidden entirely before that. -->
              <th v-if="isLocked" class="px-2.5 py-2 text-left font-semibold text-[#1b5e20] whitespace-nowrap">Status</th>
              <th class="px-2.5 py-2 text-left font-semibold text-[#1b5e20] whitespace-nowrap w-16"></th>
            </tr>
          </thead>
          <tbody>
            <!-- Empty state — a single row so the table's headers and generating-firm row still show. -->
            <tr v-if="!recipients.length && !editor.isAdding.value">
              <td class="text-center align-middle py-6 px-2.5 italic text-[#888] text-[12.5px]" :colspan="isLocked ? 8 : 7" data-cy="fee-recipients-empty">
                No recipients added. Click "+ Add Recipient" to begin.
              </td>
            </tr>

            <template v-for="recipient in recipients" :key="recipient.matterSettlementRecipientId">
              <tr :class="editor.rowErrors.value[recipient.matterSettlementRecipientId] ? '' : 'border-b border-[#e5f0e6]'" data-cy="fee-recipient-row">
                <!-- Recipient — live vendor picker pre-lock, plain text once locked (VR-015: the row is
                     fully read-only after finalization, so no disabled-looking control is rendered). -->
                <td class="px-2.5 py-2 align-middle">
                  <span v-if="isLocked" data-cy="fee-recipient-vendor-text">{{ recipient.vendorDisplayName }}</span>
                  <FeeRecipientVendorPicker
                    v-else
                    :model-value="editor.draftFor(recipient).vendorId"
                    :vendor-search="editor.vendorSearchFor(recipient)"
                    :available-vendors="editor.availableVendorsFor(recipient)"
                    data-cy="fee-recipient-vendor-input"
                    @update:model-value="(v) => { editor.draftFor(recipient).vendorId = v; editor.onFieldChange(recipient); }"
                  />
                </td>

                <!-- Role — live select pre-lock, plain text once locked. -->
                <td class="px-2.5 py-2 align-middle">
                  <span v-if="isLocked" data-cy="fee-recipient-role-text">{{ roleLabel(recipient.role) }}</span>
                  <LlSelect
                    v-else
                    :model-value="editor.draftFor(recipient).role"
                    :items="ROLE_OPTIONS"
                    item-title="label"
                    item-value="value"
                    :return-object="false"
                    density="compact"
                    hide-details
                    data-cy="fee-recipient-role-input"
                    @update:model-value="(role) => { editor.draftFor(recipient).role = role; editor.onFieldChange(recipient); }"
                  />
                </td>

                <!-- % of Fee — live text field pre-lock, plain text once locked. -->
                <td class="px-2.5 py-2 align-middle text-right tabular-nums">
                  <span v-if="isLocked" data-cy="fee-recipient-percentage-text">{{ recipient.percentage.toFixed(4) }} %</span>
                  <LlTextField
                    v-else
                    :model-value="editor.draftFor(recipient).percentage"
                    type="number"
                    :min="0"
                    :max="100"
                    step="0.0001"
                    suffix="%"
                    density="compact"
                    hide-details
                    incoming-class="text-right"
                    data-cy="fee-recipient-percentage-input"
                    @update:model-value="(v) => { editor.draftFor(recipient).percentage = Number(v); editor.onFieldChange(recipient); }"
                  />
                </td>

                <!-- Fee Amount / Expenses / Total — two-state per FRD §3.5 (UI-014a/UI-015a/UI-015):
                     "—" for every pre-finalization state, bold final amount once locked. Fee Amount and
                     Total are the firm-facing figures, so they're bold green; Expenses is supporting
                     detail, so it stays plain grey (matches the design mockup). -->
                <td class="px-2.5 py-2 align-middle text-right tabular-nums font-bold text-[#1b5e20]">{{ isLocked ? formatCurrency(recipient.feeAmount ?? 0) : '—' }}</td>
                <td class="px-2.5 py-2 align-middle text-right tabular-nums text-[#555]">{{ isLocked ? formatCurrency(recipient.expenses ?? 0) : '—' }}</td>
                <td class="px-2.5 py-2 align-middle text-right tabular-nums border-l border-[#e5f0e6] font-bold text-[#1b5e20]">{{ isLocked ? formatCurrency(recipient.total ?? 0) : '—' }}</td>

                <!-- Status — only rendered once locked (the column itself is hidden before that).
                     Once disbursements have loaded, drives Submit/Undo directly off the same
                     backend truth as the Settlement tab's Step 6 table; until then it falls back
                     to the plain badge from the recipients endpoint. -->
                <td v-if="isLocked" class="px-2.5 py-2 align-middle whitespace-nowrap" data-cy="fee-recipient-status">
                  <!-- No separate "disbursements still loading" branch here: this cell only ever
                       renders once `initialLoadPending` (below) has gone false, and that stays
                       true for as long as `isLocked && disbInitialLoad` — so by the time this `td`
                       paints, disbInitialLoad is already false and disbRowFor has real data (or a
                       genuine no-settlement miss) to match against. -->
                  <template v-if="disbRowFor(recipient.settlementFeeRecipientId)">
                    <span
                      v-if="disbRowFor(recipient.settlementFeeRecipientId)!.state === 'in-progress'"
                      class="inline-flex items-center gap-1.5 text-[11px] text-[#666]"
                      data-cy="fee-recipient-disb-spinner"
                    >
                      <v-progress-circular indeterminate size="14" width="2" color="success" />
                      {{ undoingKeys.has(disbRowFor(recipient.settlementFeeRecipientId)!.key) ? 'Undoing…' : 'Submitting…' }}
                    </span>

                    <!-- Submitted — status chip always visible; Undo sits beside it rather than
                         replacing it, mirroring the Submit/Pending pairing below. -->
                    <span
                      v-else-if="disbRowFor(recipient.settlementFeeRecipientId)!.state === 'done'"
                      class="inline-flex items-center gap-2"
                    >
                      <span class="inline-flex items-center gap-1 px-2.5 py-[3px] rounded-full text-[11px] font-semibold bg-[#dcfce7] text-[#166534] border border-[#bbf7d0]">
                        <v-icon size="12">mdi-check-circle</v-icon>
                        Submitted to QuickBooks
                      </span>
                      <LlSecondaryButton
                        v-if="disbRowFor(recipient.settlementFeeRecipientId)!.canUndo"
                        :color="AMBER"
                        variant="text"
                        size="small"
                        :disabled="disbAnyBusy"
                        :data-cy="`fee-recipient-disb-undo-${recipient.matterSettlementRecipientId}`"
                        @click="handleUndoDisbRow(disbRowFor(recipient.settlementFeeRecipientId)!)"
                      >Undo</LlSecondaryButton>
                      <LlTooltip v-else location="top">
                        <template #default>{{ disbRowFor(recipient.settlementFeeRecipientId)!.undoBlockedReason || undoBlockedReasonFallback(disbRowFor(recipient.settlementFeeRecipientId)!.key) }}</template>
                        <template #activator>
                          <span class="undo-disabled-btn">
                            <LlSecondaryButton :color="AMBER" variant="text" size="small" disabled>Undo</LlSecondaryButton>
                          </span>
                        </template>
                      </LlTooltip>
                    </span>

                    <!-- Failed — status chip beside Retry, same beside-not-replacing pairing. -->
                    <span
                      v-else-if="disbRowFor(recipient.settlementFeeRecipientId)!.state === 'error'"
                      class="inline-flex flex-col items-end gap-1"
                    >
                      <span class="inline-flex items-center gap-2">
                        <span class="inline-flex items-center gap-1 px-2.5 py-[3px] rounded-full text-[11px] font-semibold bg-[rgba(var(--v-theme-error),0.08)] text-error border border-[rgba(var(--v-theme-error),0.3)]">
                          <v-icon size="12">mdi-alert-circle</v-icon>
                          Failed
                        </span>
                        <LlSecondaryButton
                          :color="AMBER"
                          variant="flat"
                          size="small"
                          class="text-white"
                          :disabled="disbAnyBusy"
                          :data-cy="`fee-recipient-disb-retry-${recipient.matterSettlementRecipientId}`"
                          @click="handleSubmitDisbRow(disbRowFor(recipient.settlementFeeRecipientId)!)"
                        ><QboIcon size="14" class="mr-1" />Retry</LlSecondaryButton>
                      </span>
                      <span v-if="disbRowFor(recipient.settlementFeeRecipientId)!.message" class="text-[10px] text-error max-w-[180px] text-right">
                        {{ disbRowFor(recipient.settlementFeeRecipientId)!.message }}
                      </span>
                    </span>

                    <!-- Blocked — plain "Pending" chip, no reason shown and no action (e.g.
                         "Generate the settlement statement before pushing disbursements." reads
                         as broken/confusing here, since this tab has no control to do that from). -->
                    <span
                      v-else-if="disbRowFor(recipient.settlementFeeRecipientId)!.state === 'blocked'"
                      class="inline-block px-2.5 py-[3px] rounded-full text-[11px] font-semibold bg-[#f1f5f9] text-[#475569] border border-[#e2e8f0]"
                    >Pending</span>

                    <!-- Ready to submit — status chip beside Submit. -->
                    <span v-else class="inline-flex items-center gap-2">
                      <span class="inline-block px-2.5 py-[3px] rounded-full text-[11px] font-semibold bg-[#f1f5f9] text-[#475569] border border-[#e2e8f0]">Pending</span>
                      <LlSecondaryButton
                        color="primary"
                        variant="outlined"
                        size="small"
                        :disabled="disbAnyBusy"
                        :data-cy="`fee-recipient-disb-submit-${recipient.matterSettlementRecipientId}`"
                        @click="handleSubmitDisbRow(disbRowFor(recipient.settlementFeeRecipientId)!)"
                      ><QboIcon size="14" class="mr-1" />Submit</LlSecondaryButton>
                    </span>
                  </template>

                  <!-- Disbursements loaded but this recipient has no matching row (e.g. no
                       settlement yet) — fall back to the plain badge from the recipients endpoint. -->
                  <span
                    v-else
                    class="inline-flex items-center gap-1 px-2.5 py-[3px] rounded-full text-[11px] font-semibold"
                    :class="recipient.status === 'Submitted'
                      ? 'bg-[#dcfce7] text-[#166534] border border-[#bbf7d0]'
                      : 'bg-[#f1f5f9] text-[#475569] border border-[#e2e8f0]'"
                  >
                    <v-icon v-if="recipient.status === 'Submitted'" size="12">mdi-check-circle</v-icon>
                    {{ recipient.status === 'Submitted' ? 'Submitted to QuickBooks' : 'Pending' }}
                  </span>
                </td>

                <!-- Remove — the only explicit row action left; every field above autosaves on its own. -->
                <td class="px-2.5 py-2 align-middle text-center whitespace-nowrap">
                  <span v-if="editor.isSaving(recipient)" class="fr-btn-spinner" data-cy="fee-recipient-saving-spinner" />
                  <LlSecondaryButton
                    v-else-if="!isLocked"
                    icon="mdi-trash-can-outline"
                    variant="text"
                    color="error"
                    size="small"
                    title="Remove"
                    aria-label="Remove recipient"
                    data-cy="fee-recipient-remove"
                    @click="editor.remove(recipient)"
                  />
                </td>
              </tr>

              <!-- Validation message — its own full-width row rather than a div inside the Recipient
                   cell. In-cell it grew that one cell, and since every cell is `align-middle` the
                   vendor picker got re-centred against the taller row while Role/% stayed put, so the
                   controls visibly stopped lining up. As a row it also stops a %-specific message
                   ("Percentage must be greater than 0") from rendering under Recipient Name. -->
              <tr v-if="editor.rowErrors.value[recipient.matterSettlementRecipientId]" class="border-b border-[#e5f0e6]">
                <td
                  :colspan="isLocked ? 8 : 7"
                  class="px-2.5 pt-0 pb-2 text-[11px] text-error"
                  data-cy="fee-recipient-row-error"
                >
                  {{ editor.rowErrors.value[recipient.matterSettlementRecipientId] }}
                </td>
              </tr>
            </template>

            <!-- Inline "Add Recipient" row — the one exception to always-live autosave: creating still
                 needs an explicit ✓ so we don't POST a duplicate recipient on every keystroke. -->
            <tr v-if="editor.isAdding.value" data-cy="fee-recipient-add-row">
              <td class="px-2.5 py-2 align-middle">
                <FeeRecipientVendorPicker
                  v-if="editor.addDraft.value"
                  :model-value="editor.addDraft.value.vendorId"
                  :vendor-search="editor.addVendorSearch"
                  :available-vendors="editor.availableVendorsForAdd.value"
                  data-cy="fee-recipient-vendor-input"
                  @update:model-value="(v) => { if (editor.addDraft.value) editor.addDraft.value.vendorId = v; editor.onAddFieldChange(); }"
                />
              </td>
              <td class="px-2.5 py-2 align-middle">
                <LlSelect
                  v-if="editor.addDraft.value"
                  v-model="editor.addDraft.value.role"
                  :items="ROLE_OPTIONS"
                  item-title="label"
                  item-value="value"
                  :return-object="false"
                  density="compact"
                  hide-details
                  data-cy="fee-recipient-role-input"
                  @update:model-value="editor.onAddFieldChange()"
                />
              </td>
              <td class="px-2.5 py-2 align-middle text-right tabular-nums">
                <LlTextField
                  v-if="editor.addDraft.value"
                  v-model.number="editor.addDraft.value.percentage"
                  type="number"
                  :min="0"
                  :max="100"
                  step="0.0001"
                  suffix="%"
                  density="compact"
                  hide-details
                  incoming-class="text-right"
                  data-cy="fee-recipient-percentage-input"
                  @update:model-value="editor.onAddFieldChange()"
                />
              </td>
              <!-- Always "—": adding a recipient is only ever possible pre-finalization
                   (addDisabledReason blocks it once isLocked), so this row can never
                   have a real calculated value. -->
              <td class="px-2.5 py-2 align-middle text-right tabular-nums font-bold text-[#1b5e20]">—</td>
              <td class="px-2.5 py-2 align-middle text-right tabular-nums text-[#555]">—</td>
              <td class="px-2.5 py-2 align-middle text-right tabular-nums border-l border-[#e5f0e6] font-bold text-[#1b5e20]">—</td>
              <td v-if="isLocked" class="px-2.5 py-2 align-middle"></td>
              <td class="px-2.5 py-2 align-middle text-center whitespace-nowrap">
                <LlSecondaryButton
                  icon="mdi-check"
                  variant="text"
                  color="primary"
                  size="small"
                  :loading="editor.addSaving.value"
                  :disabled="editor.addSaving.value"
                  aria-label="Save recipient"
                  data-cy="fee-recipient-save"
                  @click="editor.saveAdd()"
                />
                <LlSecondaryButton
                  icon="mdi-close"
                  variant="text"
                  size="small"
                  class="ml-1.5"
                  :disabled="editor.addSaving.value"
                  aria-label="Cancel adding recipient"
                  data-cy="fee-recipient-cancel"
                  @click="editor.cancelAdd()"
                />
              </td>
            </tr>

            <!-- Add-row validation message — same full-width treatment as the saved rows above,
                 for the same reason: in-cell it knocked the vendor picker out of alignment. -->
            <tr v-if="editor.isAdding.value && editor.rowErrors.value['__new__']">
              <td
                :colspan="isLocked ? 8 : 7"
                class="px-2.5 pt-0 pb-2 text-[11px] text-error"
                data-cy="fee-recipient-row-error"
              >
                {{ editor.rowErrors.value['__new__'] }}
              </td>
            </tr>

            <!-- Generating firm — retains whatever percentage isn't allocated above; always shown. -->
            <tr class="bg-[#f1faf4] border-t-2 border-[#c6ebcf]">
              <td class="px-2.5 py-2 align-middle font-semibold text-[#1b5e20]">{{ firmName }}</td>
              <td class="px-2.5 py-2 align-middle text-xs text-[#555]">Your firm</td>
              <td class="px-2.5 py-2 align-middle text-right tabular-nums font-bold text-[#1b5e20]">{{ retainedPercentage.toFixed(4) }} %</td>
              <!-- FRD §3.5 "Generating Firm Row": same two-state rule as the recipient rows above
                   ("—" until locked, real figure after), but sourced from the settlement summary
                   rather than MatterRecipientsResponse, which carries outside recipients only.
                   Each cell falls back to "—" on a null so a missing figure never renders as a
                   $0.00 that reads like a real computed answer. -->
              <td class="px-2.5 py-2 align-middle text-right tabular-nums font-bold text-[#1b5e20]" data-cy="fee-recipient-firm-fee">
                {{ isLocked && firmFeeAmount != null ? formatCurrency(firmFeeAmount) : '—' }}
              </td>
              <td class="px-2.5 py-2 align-middle text-right tabular-nums text-[#555]" data-cy="fee-recipient-firm-expenses">
                {{ isLocked && firmExpenses != null ? formatCurrency(firmExpenses) : '—' }}
              </td>
              <td class="px-2.5 py-2 align-middle text-right tabular-nums border-l border-[#e5f0e6] font-bold text-[#1b5e20]" data-cy="fee-recipient-firm-total">
                {{ isLocked && firmTotal != null ? formatCurrency(firmTotal) : '—' }}
              </td>
              <td class="px-2.5 py-2 align-middle" :colspan="isLocked ? 2 : 1"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Persistent counterpart to the snackbar the editor raises on crossing 100%: the snackbar
           announces the moment, this stays put for as long as the allocation is still over. -->
      <div v-if="editor.isOverAllocated.value" class="mt-2.5 px-3 py-2 bg-[#fff3e0] border border-[#ffcc80] rounded text-xs text-[#e65100]" data-cy="fee-recipients-over-100-hint">
        Total recipient percentage exceeds 100%. Reduce percentages so your firm retains at least 0%.
      </div>
    </div>

    <!-- Tiered-remove reset-confirmation dialog (ERR-013a) -->
    <LlConfirmationDialog
      ref="confirmRemoveResetRef"
      title="Reset Settlement to Step 1?"
      message="This recipient has expenses recorded in an active settlement. Removing them will reset the settlement to Step 1. Remove and reset?"
      confirm-text="Remove and Reset"
      toggle-text="Cancel"
      confirm-color="error"
      :show-warning-icon="true"
      data-cy="fee-recipient-reset-confirmation"
    />

    <!-- Tiered-edit reset-confirmation dialog (sc-45849) — changing vendor/role resets the
         settlement the same way removing a recipient does, once Step 2 has been confirmed. -->
    <LlConfirmationDialog
      ref="confirmEditResetRef"
      title="Reset Settlement to Step 1?"
      message="Changing the vendor or role will reset the settlement to Step 1, since this recipient's expenses were already confirmed. Continue?"
      confirm-text="Change and Reset"
      toggle-text="Cancel"
      confirm-color="error"
      :show-warning-icon="true"
      data-cy="fee-recipient-edit-reset-confirmation"
    />

    <!-- Confirms reversing a QuickBooks disbursement — content mirrors MatterLiensTab.vue's
         Undo Submit dialog (see confirmUndo above). -->
    <LlConfirmationDialog
      ref="confirmUndoDialogRef"
      title="Undo Submit"
      :message="undoMessage"
      confirm-text="Delete Check & Undo Submit"
      toggle-text="Cancel"
      show-warning-icon
      data-cy="fee-recipient-disb-undo-dialog"
    />

    <!-- Confirms pushing a fee recipient disbursement to QuickBooks — mirrors
         LienPushConfirmDialog.vue (payee/amount preview before Submit or Retry). -->
    <FeeRecipientSubmitConfirmDialog ref="submitConfirmRef" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { storeToRefs } from 'pinia';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useFeeRecipientsStore } from '@/stores/useFeeRecipientsStore';
import { useInlineRecipientEditor, MAX_FEE_RECIPIENTS } from './feeRecipients/useInlineRecipientEditor';
import FeeRecipientVendorPicker from './feeRecipients/FeeRecipientVendorPicker.vue';
import FeeRecipientSubmitConfirmDialog from './feeRecipients/FeeRecipientSubmitConfirmDialog.vue';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import { useDisbursements } from './settlement/composables/useDisbursements';
import { feeRecipientRowKey, undoBlockedReasonFallback } from '@/utils/disbursementHelpers';
import { formatCurrency } from '@/services/utils';
import EventTracker from '@/services/eventTracker';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import AlertBar from '@/components/AlertBar.vue';
import QboIcon from '@/components/QboIcon.vue';
import type {
  ComputedBreakdown,
  ExpenseArrangement,
  FeeRecipientRole,
  MatterRecipientData as MatterRecipient,
} from '@/models/settlementTypes';
import type { DisbursementRowView } from './settlement/composables/useDisbursements';
import { EXPENSE_ARRANGEMENT } from '@/constants/settlementConstants';

const props = defineProps<{
  stateParams?: Record<string, string>;
  tracking?: object;
}>();

const ROLE_OPTIONS: { label: string; value: FeeRecipientRole }[] = [
  { label: 'Co-Counsel', value: 'coCounsel' },
  { label: 'Referring Attorney', value: 'referringAttorney' },
];

const ESTIMATE_TOOLTIP = 'Calculated when the settlement is finalized based on the recovery amount.';
/** BR-020 — only shown under Client Pays As They Go, where the figure is a net advance the firm
 *  expects reimbursed, not a straight expense total. */
const CLIENT_PAYS_EXPENSES_TOOLTIP =
  "This doesn't include any payments the client made prior to settlement. Enter only the net amount this co-counsel firm advanced without client reimbursement — this amount will be included in their disbursement at settlement.";

const clientMatterStore = useClientMatterStore();
const { features, company } = storeToRefs(useMeStore());
const store = useFeeRecipientsStore();
const { recipients, isLocked, uiState } = storeToRefs(store);

// Amber used for the disbursement Retry action — mirrors SettlementDisbursementTable.vue.
const AMBER = '#f59e0b';

const settlementStore = useSettlementStore();
const { data: settlementData } = storeToRefs(settlementStore);

const confirmUndoDialogRef = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const undoMessage = ref('');
/** Content mirrors MatterLiensTab.vue's own Undo Submit dialog (handleRowUndo) — same
 *  "delete the QuickBooks check … and revert to {priorState}" structure — since a
 *  fee-recipient undo deletes a QBO check the same way a lien undo does
 *  (CoCounselDisbursementService.ExecuteUndoAsync mirrors LienService's). Left
 *  scoped to this tab: the Step 6 Settlement Disbursement table's own Undo dialog
 *  keeps its existing simpler wording. */
async function confirmUndo(recipientLabel: string): Promise<boolean> {
  const row = disbRows.value.find((r) => r.recipientName === recipientLabel);
  undoMessage.value = `This will delete the QuickBooks check for "${recipientLabel}"${
    row ? ` — ${formatCurrency(row.amount)}` : ''
  } and revert the disbursement to Pending.`;
  if (!confirmUndoDialogRef.value) return false;
  return confirmUndoDialogRef.value.open();
}

/** Same disbursement submit/undo machinery as the Settlement tab's Step 6 table
 *  (useDisbursements) so a fee recipient can be pushed to QuickBooks straight from
 *  this tab once the settlement is locked. Matched by settlementFeeRecipientId —
 *  NOT matterSettlementRecipientId, which identifies a different (matter-level)
 *  record than the disbursement endpoints operate on. */
const {
  rows: disbRows,
  anyBusy: disbAnyBusy,
  submitRow: submitDisbRow,
  undoRow: undoDisbRow,
} = useDisbursements({ confirmUndo });

/** True until the mount-time settlement→disbursements chain (see onMounted) settles, then
 *  false forever after — deliberately NOT the same as useDisbursements' own `loading`, which
 *  flips true again on every later submit/undo refresh. Gating the Status column's spinner on
 *  that instead would flash every row back to a generic spinner on each action (the busy row
 *  already has its own "Submitting…/Undoing…" state below); this flag only covers the one-time
 *  initial gap where the recipients table has rendered but disbursement rows haven't arrived. */
const disbInitialLoad = ref(true);

const disbRowByKey = computed(() =>
  new Map<string, DisbursementRowView>(disbRows.value.map((r) => [r.key, r])),
);

function disbRowFor(settlementFeeRecipientId: string | null): DisbursementRowView | undefined {
  if (!settlementFeeRecipientId) return undefined;
  return disbRowByKey.value.get(feeRecipientRowKey(settlementFeeRecipientId));
}

/** Gates the whole-table skeleton. Once locked, stays open through `disbInitialLoad` too, so
 *  the table paints once fully-formed (recipients + real Status/Submit state) instead of in two
 *  visible waves — recipients first, then every row's Status cell repainting a beat later. Safe
 *  to combine with `isLocked` here (unlike the Status column's own spinner) because this is the
 *  one-time initial paint, not something that should re-trigger on a later submit/undo. */
const initialLoadPending = computed(
  () => (uiState.value.loading && !recipients.value.length) || (isLocked.value && disbInitialLoad.value),
);

/** The shared disbursements store marks a row in-flight the same way for submit and
 *  undo (a single `submittingKeys` list), so the 'in-progress' overlay can't tell
 *  them apart on its own. Tracked locally instead — mirrors MatterLiensTab.vue's
 *  own pushingIds/undoingIds split — so the spinner can say "Undoing…" rather than
 *  defaulting to "Submitting…" while a reversal is in flight. */
const undoingKeys = reactive(new Set<string>());

async function handleUndoDisbRow(row: DisbursementRowView): Promise<void> {
  undoingKeys.add(row.key);
  try {
    await undoDisbRow(row);
  } finally {
    undoingKeys.delete(row.key);
  }
}

/** Preview dialog before pushing to QuickBooks — mirrors MatterLiensTab.vue's
 *  handleRowPush (confirm → execute), covering both the first Submit and a
 *  post-error Retry with the same dialog. */
const submitConfirmRef = ref<InstanceType<typeof FeeRecipientSubmitConfirmDialog> | null>(null);

async function handleSubmitDisbRow(row: DisbursementRowView): Promise<void> {
  const confirmed = await submitConfirmRef.value?.open(row);
  if (!confirmed) return;
  await submitDisbRow(row);
}

const matterId = computed<string | null>(
  () => props.stateParams?.matterId ?? props.stateParams?.id ?? null,
);

const matter = ref<{ billing?: { type?: string } } | null>(null);
/** `null` until the matter fetch resolves — treated as "not disqualified yet" below. */
const isContingency = computed(() => matter.value?.billing?.type === 'contingency');
/** Defense-in-depth against direct URL nav — the tab bar already gates on this too.
 *  Permissive during the initial matter fetch so it doesn't flash-hide while loading. */
const visible = computed(
  () => !!features.value?.coCounsel && (matter.value === null || isContingency.value),
);

const confirmRemoveResetRef = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const confirmEditResetRef = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);

const editor = useInlineRecipientEditor(
  matterId,
  recipients,
  async () => !!(await confirmRemoveResetRef.value?.open()),
  async () => !!(await confirmEditResetRef.value?.open()),
);

const addDisabledReason = computed<string>(() => {
  if (isLocked.value) return 'Fee recipients are locked while a settlement is in progress.';
  if (!editor.canAddMore.value) return `Maximum of ${MAX_FEE_RECIPIENTS} fee recipients allowed`;
  if (editor.isAdding.value) return 'Finish adding this recipient first';
  return '';
});

/** Live total (draft where touched, saved otherwise) — owned by the editor so the inline hint here
 *  and the snackbar it raises can't drift apart. */
const totalPercentage = editor.totalPercentage;
const retainedPercentage = computed(() => Math.max(0, 100 - totalPercentage.value));
const firmName = computed(() => company.value?.name || 'Your Firm');

/** `null` when the matter has no settlement yet (or the fetch failed) — the recipients endpoint
 *  doesn't carry the arrangement, so it's read from the settlement store instead. */
const expenseArrangement = computed(() => settlementData.value?.expenseArrangement ?? null);

/**
 * Generating-firm figures (FRD §3.5 "Generating Firm Row"). MatterRecipientsResponse covers
 * outside recipients only — it returns nothing for the firm's own row — so these come from the
 * settlement summary instead: `generatingFirmRetainedAmount` for the fee, and `totalExpenses`
 * for expenses (the backend deliberately keeps co-counsel expenses *out* of that figure and
 * reports them separately as `coCounselExpensesTotal`, so it is already the firm's own share).
 * Total mirrors the recipient rows: fee + expenses.
 */
const settlementBreakdown = computed(() => settlementData.value?.computed ?? null);
const firmFeeAmount = computed(() => settlementBreakdown.value?.generatingFirmRetainedAmount ?? null);
const firmExpenses = computed(() => settlementBreakdown.value?.totalExpenses ?? null);
/** `null` — not 0 — while either input is missing, so the row falls back to "—" rather than
 *  showing a $0.00 that reads as a real computed answer. */
const firmTotal = computed(() =>
  firmFeeAmount.value == null && firmExpenses.value == null
    ? null
    : (firmFeeAmount.value ?? 0) + (firmExpenses.value ?? 0),
);
/** The FE alias for the backend's `ClientPays` (spec calls it ClientPaysAsTheyGo). */
const isClientPaysAsTheyGo = computed(() => expenseArrangement.value === EXPENSE_ARRANGEMENT.CLIENT);

const expensesTooltip = computed<string>(() => {
  if (isClientPaysAsTheyGo.value) return CLIENT_PAYS_EXPENSES_TOOLTIP;
  return isLocked.value ? '' : ESTIMATE_TOOLTIP;
});

function roleLabel(role: FeeRecipientRole): string {
  return ROLE_OPTIONS.find((option) => option.value === role)?.label ?? role;
}

onMounted(async () => {
  if (!matterId.value) return;
  const [fetchedMatter] = await Promise.all([
    clientMatterStore.getMatterById(matterId.value).catch((): null => null),
    // A matter with no settlement yet is the normal case here, not an error — it just means
    // there's no expense arrangement to drive the BR-020 info dot, and no disbursements to submit.
    // Chained (not awaited separately after the Promise.all below) so the disbursements read
    // races the recipients fetch instead of trailing a full extra round-trip behind it. Recipients
    // still resolve first in practice (one call vs. this two-hop chain); `initialLoadPending` keeps
    // the whole table's skeleton open through this chain (when locked) so the table paints once,
    // fully-formed, instead of recipients rendering first and every row's Status cell repainting a
    // beat later. loadDisbursements() itself no-ops if settlementId never resolved (no settlement
    // on this matter yet) — disbInitialLoad still flips false either way (the tsconfig's ES2017
    // lib has no Promise.finally, hence the onFulfilled/onRejected pair below instead) so the
    // fallback badge (correctly, since there's nothing to submit) takes over instead of spinning
    // forever.
    settlementStore
      .load(matterId.value)
      .then(() => settlementStore.loadDisbursements())
      .then(
        () => {
          disbInitialLoad.value = false;
        },
        (err) => {
          disbInitialLoad.value = false;
          throw err;
        },
      ),
    store.fetchRecipients(matterId.value),
  ]);
  matter.value = fetchedMatter as { billing?: { type?: string } } | null;

  if (props.tracking && !uiState.value.error) {
    EventTracker.trackEvent(props.tracking, 'Fee Recipients', 'Tab Viewed', {
      matterId: matterId.value,
      recipientCount: recipients.value.length,
    });
  }
});
</script>

<style scoped>
/* Everything else in this component is styled with Tailwind utility classes directly in the
   template. Two things can't be: this spinner needs a custom-timed @keyframes (Tailwind's
   built-in `animate-spin` runs at 1s, not the 0.7s used here), and `:deep()` reaches into
   child components' (LlSelect/LlAutocomplete) internal markup, which utility classes on this
   component's own template can't target. */
.fr-btn-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid #cdebd0;
  border-top-color: #29bf12;
  border-radius: 50%;
  animation: fr-spin 0.7s linear infinite;
}
@keyframes fr-spin {
  to {
    transform: rotate(360deg);
  }
}

/* LlSelect/LlAutocomplete always render a label element even when no `label` prop is passed
   (unused here); hide it so the control sits flush in the cell instead of leaving a blank
   row above it. */
:deep(.tw > label) {
  display: none;
}
:deep(.v-input),
:deep(.v-field) {
  font-size: 12.5px;
}

/* Vuetify hardcodes disabled `variant="text"` buttons to opacity:0.26 (VBtn.css) — with
   no background to fall back on, fading the whole button (text + all) reads as barely
   visible against a white row regardless of the opacity value chosen. Cancel the fade
   entirely and signal "disabled" through color alone instead: a plain muted gray, same
   weight/size as the active amber Undo, clearly legible but visually distinct from it.
   Scoped to just this instance via :deep() — other disabled buttons in the app keep the
   Vuetify default. */
.undo-disabled-btn :deep(.v-btn--disabled) {
  opacity: 1 !important;
}
.undo-disabled-btn :deep(.v-btn--disabled .v-btn__content) {
  color: #9a9a9a !important;
}
</style>
